import react, {useState, useEffect, useContext, useRef} from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { useTheme } from '@mui/material/styles';
import { Grid2 as Grid, Box, Stack, FormControl, Select, MenuItem, InputLabel, Button, IconButton, Alert, useMediaQuery, styled} from '@mui/material';
import { DataGrid, GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import '../../view-filers.css';
import { FilersDataContext } from '../../Filers';
import BaseReportType from '../../../../types/reports/baseReportType';
import { BaseReportTypeEnums } from '../../../../types/referenceData/baseReportTypeEnums';

import {
    PrincipalReportColumns,
    DepositReportColumns,
    DepositoryYearEndReportColumns,
    ReimbursementReportColumns,
    PayrollReportColumns,
    SubvendorReportColumns,
    LateContributionReportColumns,
    OtherPeriodicReportColumns,
    DonationReportColumns,
    IEPacReportFilerColumns,
    CreditCardReportColumns,
    BankExpenditureItemizationReportColumns
  } from '../../../../types/referenceData/reportTableColumns';
import { useErrorBoundary } from 'react-error-boundary';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
  

const StyledDataGrid = styled(DataGrid)(() => ({
  '& .even-row': {
    backgroundColor: '#f9f9f9',
  },
  '& .odd-row': {
    backgroundColor: '#ffffff',
  },
}));

const FilerReports = (): JSX.Element => {
    
    //initial state set up
    const theme = useTheme();
    const isMd = useMediaQuery(theme.breakpoints.up('md'), {
        defaultMatches: true,
      });
    const isLg = useMediaQuery(theme.breakpoints.up('lg'), {
        defaultMatches: true,
    });
    const isSM = useMediaQuery(theme.breakpoints.down('sm'));

    const FilerDataContext = useContext(FilersDataContext);
    const currentFiler = FilerDataContext.currentFilerPayload?.filer;
    const { showBoundary } = useErrorBoundary();

    const [reportsSummary, setReportsSummary] = useState('');
    const [tableColumns, setColumns] = useState(PrincipalReportColumns);
    const [tableRows, setRows] = useState([]);
    const [excelExportUrl, setExcelExportUrl] = useState('');
    const [txtExportUrl, setTxtExportUrl] = useState('');

    const isFirstLoad = useRef(true);

    const dropDownYears = Array.from(new Array(20), (val, index) => new Date().getFullYear() - index);
    const [baseReportTypes, setBaseReportTypes] = useState<BaseReportType[]>([
        {
            "baseReportTypeId": 1,
            "baseReportTypeDescription": "Principal Reports"
        }
    ]);
    const [baseReportType, setBaseReportType] = useState(BaseReportTypeEnums.PrincipalReports);
    const [filters, setFilters] = useState(
        {   
            reportYear: 0, 
            baseReportTypeId: baseReportType,
            reportStartDate: '',
            reportEndDate: '',
            pagesize: 50,
            startIndex: 1,
            sortField: '',
            sortDirection: 'DESC',
            withSummary: 'true'    
        });

    
    //pagination
    const [totalCount, setTotalCount] = useState(0);
    const [paginationModel, setPaginationModel] = useState({pageSize: 50, page: 0});
    
    const handlePaginationModelChange = (newPaginationModel: GridPaginationModel) => {
        setPaginationModel(newPaginationModel);
        setFilters({...filters, startIndex: (newPaginationModel.page * newPaginationModel.pageSize + 1)});
      };

    //sorting
    const [sortModel, setSortModel] = useState<GridSortModel>([
        {
          field: 'dateFiled',
          sort: 'desc',
        },
      ]);
    const handleSortModelChange = (newSortModel: GridSortModel) => {
        setSortModel(newSortModel);

        let field = newSortModel[0]?.field || '';
        let sortField = field;

        switch (field) {
            case 'reportingPeriod':
                sortField = 'End_Date';
                break;
            case 'depositSequence':
                sortField = 'ui';
                break;
            case 'startBalanceNumeric':
            case 'startBalance':
                sortField = 'Start_Balance';
                break;
            case 'netDepositTotal':
            case 'creditTotal':
            case 'receiptTotal':
            case 'receiptTotalNumeric':
                sortField = 'Receipts';
                break;
            case 'totalCharges':
            case 'expenditureTotal':
            case 'expenditureTotalNumeric':
                sortField = 'Expenditures';
                break;
            case 'totalCashOnHand':
            case 'endBalanceNumeric':
            case 'endBalance':
                sortField = 'End_Balance';
                break;
            case 'receiptItemizedTotal':
                sortField = 'Itemized_Receipts';
                break;
            case 'liabilityItemizedTotal':
                sortField = 'Liabilities';
                break;
            case 'inkindTotal':
                sortField = 'Inkinds';
                break;
            case 'expenditureItemizedTotal':
                sortField = 'Itemized_Expenses';
                break;
            case 'expenditureUnitemizedTotal':
                sortField = 'Unitemized_Expenses';
                break;
            case 'vendorName':
                sortField = 'Reimbursee';
                break;
            case 'paymentsMade':
                sortField = 'Payments';
                break;
            case 'dateFiled':
                sortField = 'Filing_Date';
                break;
        }  
        
        setFilters({...filters, sortField: sortField, sortDirection: newSortModel[0]?.sort || 'DESC'});
    };


     useEffect(() => {
        if(isFirstLoad.current){
            getFilerBaseReportList();
            isFirstLoad.current = false
        }
        else{
            getFilerReportsList();
        }
      }, [filters])

    
      const getFilerBaseReportList = async () => {

        try{
          await fetch(`${process.env.REACT_APP_API_BASE_URL}/reports/baseReportTypes/${currentFiler?.cpfId}`)
          .then(result => {
            if (!result.ok) {
              showBoundary(new Error('Failed to get filer base report types.'));
            }
            return result.json();
          })
          .then(result => {
            if(result.length > 0){
              setBaseReportTypes(result);
              setFilters({...filters, baseReportTypeId: result[0].baseReportTypeId});
              setBaseReportType(result[0].baseReportTypeId);
              updateBaseReportFilter({target: {value: result[0].baseReportTypeId}});
            }
            else{
                //no base report?
            }
          });
        }catch(error){
            console.error('Uncaught Error:', error);
        }
      }

      const getFilerReportsList = async () => {
        setRows([]);
        setReportsSummary("Loading...");

        try{
            let params = new URLSearchParams({
                ...filters,
                reportYear: filters.reportYear.toString(),
                baseReportTypeId: filters.baseReportTypeId.toString(),
                pagesize: filters.pagesize.toString(),
                startIndex: filters.startIndex.toString()
            });

            setExcelExportUrl(`${process.env.REACT_APP_API_BASE_URL}/reports/reportList/excelOutput?cpfId=${currentFiler?.cpfId}&${params.toString()}`);
            setTxtExportUrl(`${process.env.REACT_APP_API_BASE_URL}/reports/reportList/textOutput?cpfId=${currentFiler?.cpfId}&${params.toString()}`);

            await fetch(`${process.env.REACT_APP_API_BASE_URL}/reports/reportList/${currentFiler?.cpfId}?${params.toString()}`)
            .then(result => {
                if (!result.ok) {
                  showBoundary(new Error('Failed to get filer report list.'));
                }
                return result.json();
              })
            .then(result => {
                let summary = result.summary;
                if (summary.count === 0){
                    setReportsSummary("No reports found.");
                    setRows([]);
                    return;
                }
                else{
                    let count = summary.count<50? summary.count : 50;
                    setReportsSummary(`Viewing 1 through ${count} of ${summary.count} reports`);
                    setTotalCount(summary.count);
                    setRows(result.items);
                }
                
            });
    
        }catch(error){
            console.error('Uncaught Error:', error);
        }
      }

      const updateYearFilter = (event: any) => {
        setFilters({...filters, reportYear: event.target.value});
      }

      const updateBaseReportFilter = (event: any) => {
        let baseReportType = event.target.value;
        setBaseReportType(baseReportType);
        switch (baseReportType) {
            case BaseReportTypeEnums.PrincipalReports:
                setColumns(PrincipalReportColumns);
                break;
            case BaseReportTypeEnums.DepositReports:
                setColumns(DepositReportColumns);
                break;
            case BaseReportTypeEnums.DepositoryYearEndSummaries:
                setColumns(DepositoryYearEndReportColumns);
                break;
            case BaseReportTypeEnums.ReimbursementReports:
                setColumns(ReimbursementReportColumns);
                break;
            case BaseReportTypeEnums.CreditCardReport:
                setColumns(CreditCardReportColumns);
                break;
            case BaseReportTypeEnums.DepositoryYearEndSummaries:
            case BaseReportTypeEnums.PayrollItemizationReports:
                setColumns(PayrollReportColumns);
                break;
            case BaseReportTypeEnums.SubvendorReports:
                setColumns(SubvendorReportColumns);
                break;
            case BaseReportTypeEnums.BankExpenditureItemizationReports:
                setColumns(BankExpenditureItemizationReportColumns);
                break;
            case BaseReportTypeEnums.OtherPeriodicReports:
            case BaseReportTypeEnums.IEPACPeriodicReports:
            case BaseReportTypeEnums.LPCPrincipalReports:
                setColumns(OtherPeriodicReportColumns);
                break;
            case BaseReportTypeEnums.BQLateContributionReports:
            case BaseReportTypeEnums.LateContributionReports:
                setColumns(LateContributionReportColumns);
                break;
            case BaseReportTypeEnums.DonationReports:
                setColumns(DonationReportColumns);
                break;
            case BaseReportTypeEnums.IEPACReports:
                setColumns(IEPacReportFilerColumns);
        }
        
        setFilters({...filters, baseReportTypeId: event.target.value});
      }


      const updateStartDate = (date: Dayjs | null) => {
         if(dayjs(date, 'MM-DD-YYYY', true).isValid() || date === null){
           setFilters({...filters, reportStartDate: date?.toDate().toLocaleDateString() || ''});
         }
         else{
           setFilters({...filters, reportStartDate: ''});
         }
      };
 
      const updateEndDate = (date: Dayjs | null) => {
         if(dayjs(date, 'MM-DD-YYYY', true).isValid() || date === null){
           setFilters({...filters, reportEndDate: date?.toDate().toLocaleDateString() || ''});
         }
         else{
           setFilters({...filters, reportEndDate: ''});
         }
      };

    return (
    <Box sx={{ width: '100%' }} marginTop={1}>
      {/*report filter*/}
      <Grid container marginBottom={1}>
        <Grid size={{xs: 12, md: 12, lg: 10}}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ mb: 1}}>
              <Stack direction="row" spacing={0.5}>
                <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                  <InputLabel>Year</InputLabel>
                  <Select 
                    label="Report Year"
                    defaultValue={-1}
                    onChange={updateYearFilter}
                >
                  <MenuItem value={-1}>All</MenuItem>
                  {
                      dropDownYears.map((year) =>
                        <MenuItem value={year} key={year} >{year}</MenuItem>
                      )
                  }
                  </Select>
                  </FormControl>
                  <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                  <InputLabel>Type</InputLabel>
                  <Select 
                    label="Report Type"
                    value={baseReportType}
                    onChange={updateBaseReportFilter}
                  >
                  {
                    baseReportTypes?.map((type) =>
                        <MenuItem value={type.baseReportTypeId} key={type.baseReportTypeId} >{type.baseReportTypeDescription}</MenuItem>
                    )
                  }
                  </Select>
                </FormControl>
              </Stack>
              <Stack direction="row" spacing={0.5}>
                    <FormControl size="small" sx={{ m: 1, p:0, minWidth: 120 }}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker label="Start Date"
                              slotProps={
                                { textField: { size: 'small' },
                                  field: { clearable: true, onClear: () => updateStartDate(null) }
                                }
                              }
                              onChange={(newValue) => updateStartDate(newValue)}
                            />
                          </LocalizationProvider>
                      </FormControl>
                      <FormControl size="small" sx={{ m: 1, p:0, minWidth: 120 }}>
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker label="End Date"
                              slotProps={
                                { textField: { size: 'small' },
                                  field: { clearable: true, onClear: () => updateEndDate(null) } 
                                }
                              }
                              onChange={(newValue) => updateEndDate(newValue)}
                            />
                          </LocalizationProvider>
                      </FormControl>
              </Stack>
            </Stack>
           
        </Grid>
        <Grid size={{ xs: 12, md: 2 }}>
            <Stack direction="row" spacing={0.5}>
              <Button size="medium">Export:</Button>
              <IconButton aria-label="export to excel" size="small" color="success" onClick={() => window.open(excelExportUrl)}>
                <svg xmlns="http://www.w3.org/2000/svg" width="1.2em" height="1.2em" viewBox="0 0 15 15"><path fill="currentColor" d="M4.5 6.995H4v1h.5zm6 1h.5v-1h-.5zm-6 2.505H4v.5h.5zm6 0v.5h.5v-.5zm-6-6.503H4v1h.5zm6 1h.5v-1h-.5zm3-1.497h.5v-.207l-.146-.147zm-3-3l.354-.354L10.707 0H10.5zm-6 7.495h6v-1h-6zM4.5 11h6v-1h-6zm0-6.003h6v-1h-6zm8 9.003h-10v1h10zM2 13.5v-12H1v12zm11-10v10h1v-10zM2.5 1h8V0h-8zm7.646-.146l3 3l.708-.708l-3-3zM2.5 14a.5.5 0 0 1-.5-.5H1A1.5 1.5 0 0 0 2.5 15zm10 1a1.5 1.5 0 0 0 1.5-1.5h-1a.5.5 0 0 1-.5.5zM2 1.5a.5.5 0 0 1 .5-.5V0A1.5 1.5 0 0 0 1 1.5zm2 3v6h1v-6zm3 0v6h1v-6zm3 0v6h1v-6z"/></svg>
              </IconButton>
              <IconButton aria-label="export to txt file" size="small" color="primary" onClick={() => window.open(txtExportUrl)}>
                <svg xmlns="http://www.w3.org/2000/svg" width="1.2em" height="1.2em" viewBox="0 0 16 16"><path fill="currentColor" fillRule="evenodd" d="M2 1.5a.5.5 0 0 1 .5-.5H9a.5.5 0 0 1 .354.146l2.5 2.5A.5.5 0 0 1 12 4v2h-1V5H8.5a.5.5 0 0 1-.5-.5V2H3v12h8v-.5h1v1a.5.5 0 0 1-.5.5h-9a.5.5 0 0 1-.5-.5zm7 .707V4h1.793zM8.875 9.5c-.298.4-.593.803-.827 1.167a5.5 5.5 0 0 0-.374.664c-.091.2-.174.436-.174.669h1a.8.8 0 0 1 .084-.253q.102-.225.305-.54c.173-.267.383-.561.611-.87c.228.309.438.603.61.87q.203.315.306.54a1.3 1.3 0 0 1 .082.233l.002.015v.004l1 .001c0-.233-.083-.469-.174-.669a5.5 5.5 0 0 0-.374-.664a21 21 0 0 0-.827-1.167c.298-.4.593-.803.827-1.167c.147-.228.278-.454.374-.664c.091-.2.174-.435.174-.669h-1v.005l-.002.015l-.013.053a1.3 1.3 0 0 1-.069.18q-.102.225-.305.54a17 17 0 0 1-.611.87a17 17 0 0 1-.61-.87a4.5 4.5 0 0 1-.306-.54A.8.8 0 0 1 8.5 7h-1c0 .234.083.469.174.669c.096.21.227.436.374.664c.234.364.529.767.827 1.167M4 7h3v1H6v4H5V8H4zm8 0h3v1h-1v4h-1V8h-1z" clipRule="evenodd"/></svg>
             </IconButton>
            </Stack>
        </Grid>
        <Grid size={11.5}>
        <Alert icon={false} severity="info"
            sx={{ 
                display: 'flex', 
                justifyContent: 'center',
                '.MuiAlert-message': { 
                padding: '4px',
                } 
            }}
        >
            {reportsSummary}
        </Alert>
        </Grid>
      </Grid>
       {/*report table*/}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        maxWidth: isLg? '95%':'99%',
        '.MuiDataGrid-columnHeaderTitle': { 
            fontWeight: 'bold !important',
        }
        }}>
        <StyledDataGrid
             getRowClassName={(params) =>
              params.indexRelativeToCurrentPage % 2 === 0
              ? 'even-row'
              : 'odd-row'
            }
            rows={tableRows}
            columns={tableColumns}
            columnVisibilityModel={{
              reportTypeDescription: isMd? true : false,
              reportingPeriod: isMd? true : false,
              endDate: isMd? false : true,
              depositSequence: isMd? true : false,
              candidateListing: isMd? true : false,
              accountName: isMd? true : false
            }}
            getRowId={(row) => row.reportId}
            getRowHeight={() => baseReportType === BaseReportTypeEnums.IEPACReports? 'auto': isMd?40:50}
            //rowHeight={40}
            paginationModel={paginationModel}
            rowCount={totalCount}
            onPaginationModelChange={handlePaginationModelChange}
            paginationMode="server"
            pageSizeOptions={[50]}
            sortModel={sortModel}
            sortingMode="server"
            onSortModelChange={handleSortModelChange} 
            disableColumnMenu
            disableRowSelectionOnClick
            sx={{ 
              '.MuiDataGrid-cell': {
                alignItems: 'center',
                whiteSpace: 'normal', 
                wordBreak: 'break-word',
                padding: '3px!important',
              },
            }}
        />
      </Box>
    </Box>
  );
  };

  export default FilerReports;
